import { createAsyncThunk } from '@reduxjs/toolkit';
import * as React from 'react';
import { QueryClient } from 'react-query';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  CaseByIdQuery,
  InviteToCaseMutationVariables,
  useInviteToCaseMutation,
} from 'src/graphql';
import { TRootState } from 'src/store';

type InviteToCasePayload = {
  mutationInviteToCase: ReturnType<
    typeof useInviteToCaseMutation
  >['mutateAsync'];
  queryClient: QueryClient;
  setButtonState: React.Dispatch<React.SetStateAction<'default' | 'pending'>>;
  handleSubmitSuccess: () => void;
} & InviteToCaseMutationVariables & {
    userIds: number[];
    entityPermissions: string[];
  };

export const inviteToCase = createAsyncThunk<
  void,
  InviteToCasePayload,
  { state: TRootState }
>(
  'case/inviteToCase',
  async (
    {
      caseId,
      userIds,
      entityPermissions,
      note,
      mutationInviteToCase,
      handleSubmitSuccess,
      setButtonState,
      queryClient,
    }: InviteToCasePayload,
    thunkAPI,
  ) => {
    const state = thunkAPI.getState();
    const existingCaseTeam = state.case.caseById?.CaseTeam || [];

    const selectedCaseMembers = state.case.caseById?.AllCaseTeamMembers?.filter(
      (caseMember) => userIds.includes(caseMember.id),
    );

    const newCaseTeam = selectedCaseMembers?.map((caseMember) => ({
      id: caseMember.id,
      name: caseMember.name,
      site: caseMember.site,
      Permissions: entityPermissions,
      permissions: caseMember.permissions,
      Note: note
        ? {
            value: note,
            createdAt: new Date().toISOString(),
            inviterUser: { name: state.case.caseById?.CaseLead?.name },
          }
        : null,
      invitedAt: new Date().toISOString(),
    }));

    const queryKey = ['CaseById', { id: caseId }];
    queryClient.cancelQueries(queryKey);
    queryClient.setQueryData<CaseByIdQuery | undefined>(queryKey, (oldData) => {
      if (!oldData?.caseById) return oldData;

      return {
        ...oldData,
        caseById: {
          ...oldData.caseById,
          CaseTeam: [...oldData.caseById.CaseTeam, ...(newCaseTeam ?? [])],
        },
      };
    });

    try {
      setButtonState('pending');

      await mutationInviteToCase({
        caseId,
        userIds,
        entityPermissions,
        note,
      });

      setButtonState('default');
      handleSubmitSuccess();
      handleSuccessToast({
        toastMessage: 'Success',
        toastDescription: `You have added ${userIds.length} ${
          userIds.length === 1 ? 'person' : 'people'
        }`,
      });
    } catch (e) {
      setButtonState('default');
      queryClient.setQueryData<CaseByIdQuery | undefined>(
        queryKey,
        (oldData) => {
          if (!oldData?.caseById) return oldData;

          return {
            ...oldData,
            caseById: {
              ...oldData.caseById,
              CaseTeam: existingCaseTeam,
            },
          };
        },
      );

      handleErrorToast({
        toastMessage: 'Oops! Something went wrong',
        toastDescription: `Sorry, we can't send your invitation now. Please check your connection and try again later.`,
      });
    } finally {
      queryClient.invalidateQueries(queryKey);
    }
  },
);
